// @ts-nocheck
import { useEffect, useState, type ReactNode } from "react";
import { CheckCircle2, Facebook, Instagram, Loader2, XCircle } from "lucide-react";
import { api, type CarouselItem } from "@/lib/api";
import PublishJobProgress from "@/components/PublishJobProgress";
import { CtaIo, StudioSplit } from "@/components/studioTabs";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { OP_SURFACE } from "@/lib/opSurfaces";
import { OpRoleBadge } from "@/components/op/OpRoleBadge";
import {
  hasFbPublished,
  hasIgPublished,
  platformPostHref,
  publishPlatformStatus,
  publishStateBadge,
  resolvePublishChannel,
  type PublishPlatformStatus,
} from "@/lib/publishPlatformStatus";

import * as Helpers from "../helpers";
const { CarouselQueueList } = Helpers;

const DAY_NAMES = ["niedziela", "poniedziałek", "wtorek", "środa", "czwartek", "piątek", "sobota"];

/** datetime-local albo ISO → czytelny termin PL (Warszawa). */
function formatScheduleLabel(raw: string): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  const normalized = text.length === 16 && text.includes("T") ? `${text}:00` : text;
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) {
    return text.replace("T", " ");
  }
  const day = DAY_NAMES[dt.getDay()] || "";
  const dd = dt.getDate();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${day} ${dd}.${mm}.${yyyy}, ${hh}:${min}`;
}

function toDatetimeLocalValue(raw: string): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text)) return text.slice(0, 16);
  const dt = new Date(text);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export default function PublishTab({ ctx }: { ctx: any }) {
  const {
    askDelete,
    clickAi,
    deleteM,
    fromPubM,
    goTab,
    item,
    listQ,
    openExisting,
    publishBusy,
    publishPlatformM,
    publishRunning,
    qaBlocks,
    rankingQ,
    ratingM,
    recomposeM,
    republishM,
    retryPublishM,
    retroM,
    scheduleM,
    selectedId,
    setSelectedId,
    setWhen,
    slot,
    studio,
    fbPublishMode,
    setFbPublishMode,
    when,
  } = ctx;
  const [publishIg, setPublishIg] = useState(true);
  const [publishFb, setPublishFb] = useState(true);

  useEffect(() => {
    setPublishIg(!hasIgPublished(item));
    setPublishFb(!hasFbPublished(item));
  }, [item?.id, item?.ig_permalink, item?.ig_media_id, item?.fb_permalink, item?.fb_post_id]);

  useEffect(() => {
    if (item?.status === "scheduled" && item?.scheduled_at) {
      const local = toDatetimeLocalValue(item.scheduled_at);
      if (local) setWhen(local);
    }
  }, [item?.id, item?.status, item?.scheduled_at, setWhen]);

  const slides = item?.slides || [];
  const bust = item?.updated_at || item?.render_done || "";
  const isScheduled = item?.status === "scheduled" && Boolean(item?.scheduled_at);
  const scheduleLabel = formatScheduleLabel(item?.scheduled_at || when);
  const showProgress =
    publishRunning ||
    item?.status === "publishing" ||
    Boolean(item?.publish_progress?.steps?.length) ||
    Boolean(item?.publish_error);
  const igStatus = publishPlatformStatus(item, "ig");
  const fbStatus = publishPlatformStatus(item, "fb");
  const resultTone =
    item?.status === "publishing" || publishRunning
      ? "publishing"
      : item?.publish_error || item?.status === "failed"
        ? "error"
        : hasIgPublished(item) || hasFbPublished(item)
          ? "done"
          : "idle";

  return (
    <TabsContent value="publish" className="mt-0">
      <StudioSplit
        left={
          <Card id="publish" className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-900">Publikacja</CardTitle>
              <p className="text-sm text-muted-foreground">
                Podgląd PNG, status Graph i linki do postów. Każda platforma osobno albo obie naraz.
              </p>
            </CardHeader>
            <CardContent className="space-y-5">
              {item && slides.length ? (
                <div className="space-y-2">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Wysyłane slajdy
                  </p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {slides.map((slide) => (
                      <figure key={slide.id || slide.index_n} className="w-[6.5rem] shrink-0">
                        <img
                          src={`${api.carouselSlidePng(item.id, slide.index_n)}?v=${bust}`}
                          alt={`slajd ${slide.index_n}`}
                          className={cn(
                            "aspect-[4/5] h-28 w-full rounded border bg-muted object-cover",
                            slide.qa_ok === false ? "border-2 border-red-600" : "border-border",
                          )}
                        />
                        <figcaption className="mt-1 truncate text-[11px] text-muted-foreground">
                          {slide.index_n}. {slide.slide_type || "slajd"}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-4 text-sm text-muted-foreground">
                  Brak złożonych PNG. Najpierw złóż obrazki w zakładce Wyjście.
                </p>
              )}

              <PublishResultBlock item={item} ig={igStatus} fb={fbStatus} tone={resultTone} />

              {showProgress ? (
                <div className="rounded-lg border border-border bg-muted/10 p-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Przebieg Graph
                  </p>
                  <PublishJobProgress
                    item={item}
                    running={publishRunning || item?.status === "publishing"}
                    error={
                      publishPlatformM.error instanceof Error
                        ? publishPlatformM.error.message
                        : republishM.error instanceof Error
                          ? republishM.error.message
                          : retryPublishM.error instanceof Error
                            ? retryPublishM.error.message
                            : undefined
                    }
                  />
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <PlatformPublishCard
                  status={igStatus}
                  disabled={!item || publishBusy || qaBlocks}
                  canPublish={Boolean(studio?.publish?.can_publish_ig)}
                  capHint={
                    !studio?.publish?.can_publish_ig
                      ? "Brak scope instagram_content_publish."
                      : undefined
                  }
                  onPublish={() =>
                    clickAi("publish", () => publishPlatformM.mutate({ channel: "ig", force: false }))
                  }
                  onRepublish={() =>
                    clickAi("publish", () => publishPlatformM.mutate({ channel: "ig", force: true }))
                  }
                  publishing={publishPlatformM.isPending}
                />
                <PlatformPublishCard
                  status={fbStatus}
                  disabled={!item || publishBusy || qaBlocks}
                  canPublish={Boolean(studio?.publish?.can_publish_fb)}
                  capHint={
                    studio?.publish?.can_publish_fb && !studio.publish.using_page_token
                      ? studio.publish.fb_hint || "FB wymaga tokena strony."
                      : !studio?.publish?.can_publish_fb
                        ? "Brak scope pages_manage_posts."
                        : undefined
                  }
                  fbFormatSlot={
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-foreground">Format Facebooka</label>
                      <Select
                        value={fbPublishMode}
                        onChange={setFbPublishMode}
                        aria-label="Format publikacji na Facebooku"
                        options={[
                          { value: "gallery", label: "Galeria" },
                          { value: "slide1", label: "Slajd 1" },
                          { value: "reel", label: "Rolka 9:16" },
                        ]}
                      />
                    </div>
                  }
                  onPublish={() =>
                    clickAi("publish", () => publishPlatformM.mutate({ channel: "fb", force: false }))
                  }
                  onRepublish={() =>
                    clickAi("publish", () => publishPlatformM.mutate({ channel: "fb", force: true }))
                  }
                  publishing={publishPlatformM.isPending}
                />
              </div>

              <div className="rounded-md border border-dashed border-border bg-muted/10 p-3 space-y-2">
                <p className="text-sm font-medium text-foreground">Publikuj wybrane platformy</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="rounded border-border"
                      checked={publishIg}
                      onChange={(e) => setPublishIg(e.target.checked)}
                    />
                    Instagram
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="rounded border-border"
                      checked={publishFb}
                      onChange={(e) => setPublishFb(e.target.checked)}
                    />
                    Facebook
                  </label>
                </div>
                <Button
                  size="sm"
                  variant="brand"
                  disabled={
                    !item ||
                    publishBusy ||
                    recomposeM.isPending ||
                    qaBlocks ||
                    !resolvePublishChannel(publishIg, publishFb)
                  }
                  onClick={() => {
                    const channel = resolvePublishChannel(publishIg, publishFb);
                    if (!channel) return;
                    recomposeM.mutate(undefined, {
                      onSuccess: () =>
                        clickAi("publish", () => publishPlatformM.mutate({ channel, force: false })),
                    });
                  }}
                >
                  {publishBusy || recomposeM.isPending ? "Publikuję…" : "Publikuj teraz (wybrane)"}
                </Button>
              </div>

              {slot?.label ? (
                <div className="rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
                  <div className="font-medium text-foreground">Propozycja: {slot.label}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{slot.reason}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Okna: wt/śr/pt 10:00, potem 19:00. Jeden dzień, jedna karuzela. Północ nie jest oknem.
                  </p>
                  {when !== slot.local ? (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setWhen(slot.local)}>
                      Wstaw {slot.label}
                    </Button>
                  ) : null}
                </div>
              ) : null}

              <div id="studio-primary-cta" className="flex flex-wrap gap-2">
                <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
                <Button
                  size="sm"
                  variant={isScheduled ? "outline" : "brand"}
                  disabled={!item || !when || scheduleM.isPending}
                  onClick={() => clickAi("publish", () => scheduleM.mutate(false))}
                >
                  {scheduleM.isPending
                    ? "Planuję…"
                    : isScheduled
                      ? "Zmień termin"
                      : "Zaplanuj"}
                </Button>
              </div>

              {isScheduled || scheduleM.isSuccess ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-950">
                  <p className="font-medium">
                    {isScheduled ? "Zaplanowana" : "Zapisano w kolejce"}
                    {scheduleLabel ? `: ${scheduleLabel}` : ""}
                  </p>
                  <p className="mt-1 text-xs text-emerald-900/80">
                    Celery opublikuje samo o tej godzinie (czas Warszawa). Nic nie klikaj w momencie startu.
                    Możesz zmienić termin powyżej i kliknąć „Zmień termin”.
                  </p>
                  {item?.celery_task_id ? (
                    <p className="mt-1 text-[11px] text-emerald-900/60">
                      Zadanie w kolejce: tak
                    </p>
                  ) : isScheduled ? (
                    <p className="mt-1 text-[11px] text-amber-800">
                      Brak celery_task_id. Termin jest w bazie; przy awarii Beata publikacja może nie wystartować sama.
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Po „Zaplanuj” status zmieni się na zaplanowana. Publikacja idzie automatycznie o wybranej godzinie.
                </p>
              )}

              <CtaIo io="publish" />

              {scheduleM.error || publishPlatformM.error || republishM.error || retryPublishM.error ? (
                <p className="text-sm text-destructive">
                  {(scheduleM.error || publishPlatformM.error || republishM.error || retryPublishM.error) instanceof
                  Error
                    ? (
                        (scheduleM.error ||
                          publishPlatformM.error ||
                          republishM.error ||
                          retryPublishM.error) as Error
                      ).message
                    : "Publikacja nie wystartowała"}
                </p>
              ) : null}
              {item?.publish_error ? <p className="text-sm text-destructive">{item.publish_error}</p> : null}
              {!studio?.publish?.can_publish_ig && (
                <p className="text-xs text-warning">
                  Token bez instagram_content_publish. Kolejka działa, publikacja Graph nie.
                </p>
              )}
              {studio?.publish?.can_publish_fb && !studio.publish.using_page_token ? (
                <p className="text-xs text-warning">
                  {studio.publish.fb_hint ||
                    "FB unpublished wymaga tokena strony. IG może przejść, post na Page padnie z #200."}
                </p>
              ) : null}
            </CardContent>
          </Card>
        }
        right={
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-base text-slate-900">Kolejka</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <CarouselQueueList
                items={listQ.data?.items || []}
                activeId={selectedId}
                empty="Kolejka pusta."
                onOpen={openExisting}
                onNext={(id) => fromPubM.mutate(id)}
                onRate={(id, rating) => ratingM.mutate({ rating })}
                onRetro={(id) => clickAi("retro", () => retroM.mutate({ id, phase: "discussion" }))}
                onDelete={askDelete}
                onRetry={(row) => {
                  setSelectedId(row.id);
                  clickAi("publish", () => retryPublishM.mutate({ id: row.id }));
                }}
                retryPendingId={retryPublishM.isPending ? retryPublishM.variables?.id : undefined}
                deletePendingId={deleteM.isPending ? deleteM.variables : undefined}
              />
              {!!rankingQ.data?.items?.length && (
                <div className="rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  Ranking: {rankingQ.data.items.length} pozycji z insightami.
                </div>
              )}
            </CardContent>
          </Card>
        }
      />

      {item?.status === "published" ? (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-base text-slate-900">Wyniki</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={() => goTab("wyniki")}>
              Otwórz zakładkę Wyniki
            </Button>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <div className="grid gap-2 sm:grid-cols-4">
              <span>Reach: {item?.insights?.reach ?? "-"}</span>
              <span>Saves: {item?.insights?.saved ?? "-"}</span>
              <span>Via: {item?.insights?.clicks_via ?? "-"}</span>
              <span>Score: {item?.insights?.score ?? "-"}</span>
            </div>
            <p>
              {item?.retro_results?.id
                ? `Wnioski z wyników: gotowe (${(item.lessons || []).filter((l: any) => l.source === "results" && l.status === "pending").length} pending).`
                : "Wnioski z wyników: auto ~48 h po insights (albo ręcznie na zakładce Wyniki)."}
            </p>
          </CardContent>
        </Card>
      ) : null}
    </TabsContent>
  );
}

function PublishResultBlock({
  item,
  ig,
  fb,
  tone,
}: {
  item?: CarouselItem | null;
  ig: PublishPlatformStatus;
  fb: PublishPlatformStatus;
  tone: "idle" | "publishing" | "done" | "error";
}) {
  if (!item) return null;
  const label =
    tone === "publishing"
      ? "Publikuję…"
      : tone === "error"
        ? "Błąd publikacji"
        : tone === "done"
          ? "Gotowe"
          : "Jeszcze nie wysłane";
  const Icon =
    tone === "publishing" ? Loader2 : tone === "error" ? XCircle : tone === "done" ? CheckCircle2 : null;
  return (
    <div
      className={cn(
        "space-y-3 p-3",
        tone === "done"
          ? OP_SURFACE.result.box
          : tone === "error"
            ? OP_SURFACE.danger.box
            : tone === "publishing"
              ? OP_SURFACE.process.box
              : OP_SURFACE.work.box,
      )}
    >
      <div className="flex items-center gap-2">
        <OpRoleBadge
          tone={tone === "done" ? "result" : tone === "error" ? "danger" : tone === "publishing" ? "process" : "work"}
          label={label}
        />
        {Icon ? (
          <Icon
            className={cn(
              "h-4 w-4",
              tone === "publishing" && "animate-spin text-cyan-700",
              tone === "error" && "text-destructive",
              tone === "done" && "text-indigo-700",
            )}
          />
        ) : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <PlatformResultLink status={ig} item={item} />
        <PlatformResultLink status={fb} item={item} />
      </div>
    </div>
  );
}

function PlatformResultLink({
  status,
  item,
}: {
  status: PublishPlatformStatus;
  item: CarouselItem;
}) {
  const href = platformPostHref(status.id, item);
  const Icon = status.id === "ig" ? Instagram : Facebook;
  const badge = publishStateBadge(status.uiState);
  return (
    <div className="rounded-md border border-border/80 bg-white/80 px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          {status.label}
        </span>
        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", badge.className)}>
          {badge.text}
        </span>
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="text-sm font-medium text-brand hover:underline"
        >
          Otwórz post
        </a>
      ) : status.uiState === "publishing" ? (
        <p className="text-sm text-muted-foreground">W toku…</p>
      ) : status.uiState === "skipped" ? (
        <p className="text-sm text-muted-foreground">Pominięte w tej wysyłce</p>
      ) : status.mediaId ? (
        <p className="text-xs text-muted-foreground">ID: {status.mediaId}</p>
      ) : (
        <p className="text-sm text-muted-foreground">Brak linku</p>
      )}
    </div>
  );
}

function PlatformPublishCard({
  status,
  disabled,
  canPublish,
  capHint,
  fbFormatSlot,
  onPublish,
  onRepublish,
  publishing,
}: {
  status: PublishPlatformStatus;
  disabled?: boolean;
  canPublish?: boolean;
  capHint?: string;
  fbFormatSlot?: ReactNode;
  onPublish: () => void;
  onRepublish: () => void;
  publishing?: boolean;
}) {
  const badge = publishStateBadge(status.uiState);
  const Icon = status.id === "ig" ? Instagram : Facebook;
  return (
    <div className="rounded-lg border border-border bg-muted/10 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{status.label}</span>
        </div>
        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", badge.className)}>
          {badge.text}
        </span>
      </div>
      {status.permalink ? (
        <a
          href={status.permalink}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-brand hover:underline break-all"
        >
          Otwórz post
        </a>
      ) : null}
      {status.mediaId ? (
        <p className="text-[11px] text-muted-foreground">ID: {status.mediaId}</p>
      ) : null}
      {status.detail && status.uiState === "error" ? (
        <p className="text-xs text-destructive">{status.detail}</p>
      ) : null}
      {capHint ? <p className="text-xs text-warning">{capHint}</p> : null}
      {fbFormatSlot}
      <div className="flex flex-wrap gap-2 pt-1">
        {!status.live ? (
          <Button
            size="sm"
            variant="brand"
            disabled={disabled || !canPublish || publishing}
            onClick={onPublish}
          >
            {publishing ? "Wysyłam…" : "Opublikuj"}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled || !canPublish || publishing}
            onClick={onRepublish}
          >
            {publishing ? "Wysyłam…" : "Opublikuj ponownie"}
          </Button>
        )}
      </div>
    </div>
  );
}
